/**
 * `ProviderCardShell` — the collapsible provider-card chrome shared by every
 * integration card on Workspace Settings → Integrations (GitHub, Slack, and
 * the issue tracker).
 *
 * Extracted from `Integrations.tsx` (#1165 introduced it there for the GitHub
 * and Slack cards). The issue-tracker card (#1558) is the third consumer, so
 * the shell moved to its own module — importing it back from `Integrations.tsx`
 * would create a cycle, since that page imports `IssueTrackerCard`. This is the
 * "promote to a shared primitive when a second use site appears" note the
 * original in-file component carried.
 *
 * Wraps a card's body in a native `<details>` element so the operator can
 * collapse a provider they don't care about today. The card header (provider
 * name + connection summary) lives in the `<summary>` so it stays clickable
 * when collapsed; the body (connection details or the empty-state CTA) lives
 * below.
 *
 * Why native `<details>` instead of a Radix-style Collapsible: the integrations
 * surface hand-rolls its primitives (Tooltip, ProviderBlock) rather than
 * pulling Radix; native disclosure is accessible by default (Tab to focus,
 * Enter/Space to toggle), zero new dep, and the styling we need is small.
 *
 * The `open` prop is controlled (the parent drives it from local state) so a
 * default-open heuristic can initialize once and the operator's manual toggle
 * wins after. `onOpenChange` fires on every toggle so the parent stays in sync.
 */
import { ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'

export function ProviderCardShell({
  headingId,
  providerName,
  summary,
  open,
  onOpenChange,
  children,
  /** Optional className spaced onto the inner `<details>`. */
  className,
}: {
  headingId: string
  providerName: string
  summary: ReactNode
  open: boolean
  onOpenChange: (next: boolean) => void
  children: ReactNode
  className?: string
}) {
  return (
    <details
      open={open}
      // `currentTarget` is the <details> element this listener is bound to.
      // Reading `target` here is incorrect — bubbling events from <summary> or
      // any nested element would yield the wrong `.open` value (or undefined)
      // and break the controlled-open sync.
      onToggle={(e) => onOpenChange(e.currentTarget.open)}
      className={`group rounded-lg border bg-card ${className ?? ''}`}
      data-testid={`provider-card-${providerName.toLowerCase()}`}
    >
      <summary
        className="flex items-center justify-between gap-3 cursor-pointer list-none px-4 py-3 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        aria-controls={`${headingId}-body`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <h3 id={headingId} className="text-sm font-semibold">
            {providerName}
          </h3>
          <span className="text-xs">{summary}</span>
        </div>
        {/* Chevron rotates via the `[open]` selector below. */}
        <ChevronDown
          className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div id={`${headingId}-body`} className="border-t px-4 py-3 space-y-4">
        {children}
      </div>
    </details>
  )
}
